import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { getIncident, reviewIncident, type IncidentDetail as IncidentDetailType, type ReviewVerdict } from '../../services/incidents';
import { AttackCategoryBadge } from '../../components/AttackCategoryBadge';
import { SeverityIndicator } from '../../components/SeverityIndicator';
import { CrossCorrelationLinks } from '../../components/CrossCorrelationLinks';
import { humanizarEvento, corDoTom, rotuloDoTom, resumirIncidente, contagemLegivel } from '../../lib/eventHumanizer';

export function IncidentDetail() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<IncidentDetailType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verdict, setVerdict] = useState<ReviewVerdict>('ok');
  const [notes, setNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  async function load() {
    if (!uuid) return;
    setLoading(true);
    try {
      const d = await getIncident(uuid);
      setData(d);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [uuid]);

  async function submitReview() {
    if (!uuid) return;
    setReviewing(true);
    try {
      await reviewIncident(uuid, { verdict, notes: notes || undefined });
      await load();  // refresh
      setNotes('');
    } catch (e) {
      setError(String(e));
    } finally {
      setReviewing(false);
    }
  }

  if (loading) return <div className="p-6">Loading…</div>;
  if (error) return <div className="p-6 text-red-400">Error: {error}</div>;
  if (!data) return <div className="p-6">No data</div>;

  return (
    <div className="p-6 space-y-4">
      <div>
        <button onClick={() => navigate('/incidents')} className="text-blue-400 hover:underline text-sm">
          ← Back to Incidents
        </button>
      </div>

      <h1 className="text-2xl font-bold flex items-center gap-3">
        <AttackCategoryBadge category={data.attack_category} />
        Incident <span className="font-mono text-base text-gray-400">{data.id}</span>
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-900 p-4 rounded border border-gray-800">
          <h2 className="text-sm uppercase text-gray-400 mb-2">Overview</h2>
          <div className="space-y-2 text-sm">
            <div><span className="text-gray-400">Status:</span> {data.status}</div>
            <div><span className="text-gray-400">Severity max:</span> <SeverityIndicator severity={data.severity_max} /></div>
            <div><span className="text-gray-400">Events:</span> {data.event_count}</div>
            <div><span className="text-gray-400">Occurrences total:</span> {data.occurrence_sum}</div>
            <div><span className="text-gray-400">First seen:</span> {data.first_seen_at}</div>
            <div><span className="text-gray-400">Last seen:</span> {data.last_seen_at}</div>
            {data.summary && <div><span className="text-gray-400">Summary:</span> {data.summary}</div>}
          </div>
        </div>

        <div className="bg-gray-900 p-4 rounded border border-gray-800">
          <h2 className="text-sm uppercase text-gray-400 mb-2">Target</h2>
          <div className="space-y-2 text-sm">
            <div><span className="text-gray-400">Session id:</span> <span className="font-mono">{data.session_id}</span></div>
            <div><span className="text-gray-400">HWID:</span> <span className="font-mono">{data.hwid || '—'}</span></div>
            <div><span className="text-gray-400">Account:</span> {data.account_id || '—'}</div>
            <div><span className="text-gray-400">IP:</span> <span className="font-mono">{data.source_ip || '—'}</span></div>
          </div>
          <div className="mt-3">
            <CrossCorrelationLinks hwid={data.hwid} account_id={data.account_id} ip={data.source_ip} />
          </div>
        </div>
      </div>

      {!data.reviewed_at && (
        <div className="bg-gray-900 p-4 rounded border border-gray-800">
          <h2 className="text-sm uppercase text-gray-400 mb-2">Review</h2>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="block text-xs text-gray-400">Verdict</label>
              <select value={verdict} onChange={(e) => setVerdict(e.target.value as ReviewVerdict)}
                className="bg-gray-800 text-gray-100 px-2 py-1 rounded">
                <option value="ok">OK (true positive válido)</option>
                <option value="fp">FP (false positive)</option>
                <option value="escalate">Escalate</option>
              </select>
            </div>
            <div className="flex-grow">
              <label className="block text-xs text-gray-400">Notes</label>
              <input
                type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Reason / evidence link / etc"
                className="bg-gray-800 text-gray-100 px-2 py-1 rounded w-full"
              />
            </div>
            <button onClick={submitReview} disabled={reviewing}
              className="px-4 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white">
              {reviewing ? 'Saving…' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {data.reviewed_at && (
        <div className="bg-emerald-900/30 p-3 rounded border border-emerald-700 text-sm">
          ✓ Reviewed at {data.reviewed_at} {data.reviewed_notes && `— ${data.reviewed_notes}`}
        </div>
      )}

      {/* RESUMO — o operador precisa entender o incidente sem ler a tabela.
          Eventos repetidos são agrupados e ordenados por GRAVIDADE (e não por
          volume): 1 driver de cheat importa mais que 40 varreduras de handle,
          e ordenar por contagem enterraria o sinal forte embaixo do ruído. */}
      {(() => {
        const r = resumirIncidente(data.evidence_events ?? []);
        return (
          <div className="bg-[#0d0f12] border border-[#2a2f36] rounded p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <h2 className="text-base text-gray-100 font-medium">{r.titulo}</h2>
              <span className={`text-xs px-2 py-0.5 rounded border shrink-0 ${corDoTom(r.tom)}`}>
                {rotuloDoTom(r.tom)}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {contagemLegivel(data.event_count, data.occurrence_sum)}
              {' · '}
              {new Date(data.first_seen_at).toLocaleString()} até{' '}
              {new Date(data.last_seen_at).toLocaleString()}
            </p>
            {r.soOperacional && (
              <p className="text-xs text-gray-500 mt-2 italic">
                Nada neste incidente é conduta do jogador — é ruído do sistema
                (rede, configuração ou versão não cadastrada).
              </p>
            )}
            {r.linhas.length > 0 && (
              <ul className="mt-3 space-y-1">
                {r.linhas.map((l) => (
                  <li key={l.titulo} className="flex items-start gap-2 text-sm">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded border shrink-0 ${corDoTom(l.tom)}`}>
                      {l.vezes}×
                    </span>
                    <span className="text-gray-200">{l.titulo}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })()}

      <div>
        <h2 className="text-sm uppercase text-gray-400 mb-2">Eventos que compõem ({data.evidence_events.length})</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border border-gray-800">
            <thead className="bg-gray-900 text-gray-300">
              <tr>
                <th className="text-left p-2">O que aconteceu</th>
                <th className="text-left p-2">Gravidade</th>
                <th className="text-right p-2">Vezes</th>
                <th className="text-left p-2">Primeiro</th>
                <th className="text-left p-2">Último</th>
                <th className="text-left p-2">Programa de origem</th>
                <th className="text-left p-2">Dados técnicos</th>
              </tr>
            </thead>
            <tbody>
              {data.evidence_events.map((e) => (
                <tr key={e.id} className="border-t border-gray-800">
                  <td className="p-2">
                    {/* O que aconteceu, em português — o nome técnico fica
                        abaixo, menor, para quem for investigar a fundo. */}
                    {(() => {
                      const h = humanizarEvento(e.event_type, e.payload);
                      return (
                        <>
                          <span className={`inline-block text-[11px] px-1.5 py-0.5 rounded border mr-2 align-middle ${corDoTom(h.tom)}`}>
                            &nbsp;
                          </span>
                          <span className="text-sm text-gray-100">{h.titulo}</span>
                          {h.detalhe && (
                            <span className="block text-xs text-gray-400 mt-0.5">{h.detalhe}</span>
                          )}
                          <code className="block text-[11px] text-gray-600 mt-0.5">{e.event_type}</code>
                        </>
                      );
                    })()}
                  </td>
                  <td className="p-2"><SeverityIndicator severity={e.severity} showLabel={false} /></td>
                  <td className="p-2 text-right">{e.occurrence_count ?? 1}</td>
                  <td className="p-2 text-xs">
                    {new Date(e.first_seen_at ?? e.created_at).toLocaleString()}
                  </td>
                  <td className="p-2 text-xs">
                    {new Date(e.last_seen_at ?? e.created_at).toLocaleString()}
                  </td>
                  <td className="p-2 text-xs">{e.source_program?.name || '—'}</td>
                  <td className="p-2"><pre className="text-xs text-gray-400 max-w-xs truncate">{JSON.stringify(e.payload)}</pre></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default IncidentDetail;
